/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import React from 'react'
import './PatientSection.scss'
import { EligibilityRequestDetailsComponent } from '../EligibilityRequestDetails/EligibilityRequestDetails'
import { IIndividual, IBaseResponse, IDemographic, IDemographicIndividualResponse } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { Container, Col, Row } from 'reactstrap'
import { CommonUtils } from '../../../../utils/CommonUtils'
import fileSaver from 'file-saver'

export interface IPatientSectionProps {
	sentSubscriberDetails?: IIndividual
	sentGuarantorDetails?: IIndividual
	receivedSubscriberDetails?: IIndividual
	demographicResponse?: IDemographic
	response?: IBaseResponse
}

export class PatientSection extends React.Component<IPatientSectionProps> {
	private sentGuarantor
	private sent
	render(): React.ReactNode {
		let demographicRedFlags
		let demographicWarnings
		let demographicConfidenceScore
		let showDVLabel = false
		let showPayorLabel = false
		this.sent = this.props.sentSubscriberDetails
		this.sentGuarantor = this.props.sentGuarantorDetails
		const received = this.props.receivedSubscriberDetails
		const demographicResponse = this.props.demographicResponse
		const demographicIndividual = {} as IDemographicIndividualResponse
		const demographicGuarantor = {} as IDemographicIndividualResponse
		const demographicData = {} as IDemographicIndividualResponse

		if (demographicResponse) {
			if (demographicResponse.guarantor && demographicResponse.guarantorId) {
				demographicData.individual = demographicResponse.guarantor
				demographicGuarantor.individual = demographicResponse.guarantor
				demographicGuarantor.id = demographicResponse.guarantorId
				demographicGuarantor.confidenceScore = demographicResponse.confidenceScore
				demographicGuarantor.Deceased = demographicResponse.guarantorDeceased
			} else {
				demographicIndividual.individual = demographicResponse.individual
				demographicIndividual.id = demographicResponse.individualId
				demographicIndividual.confidenceScore = demographicResponse.confidenceScore
				demographicIndividual.Deceased = demographicResponse.guarantorDeceased
			}
			demographicWarnings = demographicResponse.warnings
			demographicRedFlags = demographicResponse.redFlags
			if (this.sentGuarantor && demographicGuarantor.id) {
				showDVLabel = this.setDVLabel(this.sentGuarantor, demographicGuarantor.individual)
				demographicConfidenceScore = demographicGuarantor.confidenceScore && demographicGuarantor.confidenceScore.trim()
			} else {
				showDVLabel = this.setDVLabel(this.sent, demographicIndividual.individual)
				demographicConfidenceScore = demographicIndividual.confidenceScore && demographicIndividual.confidenceScore.trim()
			}
		}
		showPayorLabel = this.setPayorLabel(this.sent, received)
		if (!this.sent) {
			return null
		}

		return (
			<Container className="patientSectionPrintFix" style={{ margin: '0 0 1.3em 0', width: '97%' }}>
				<Row>
					<Col xs="6" style={{ padding: '0px' }} className="requestIndividual">
						<EligibilityRequestDetailsComponent transactionRequestDetails={this.sent} demographicResponse={demographicIndividual} title="Request" />
					</Col>
					<Col xs="6" style={{ padding: '0px' }} className="responseIndividual">
						{received ? this.renderPatientResponseDetails(this.sent, received) : null}
					</Col>
				</Row>
				<Row style={{ marginTop: '0px', display: this.props.sentGuarantorDetails ? '' : 'none' }}>
					<Col xs="6" style={{ padding: '0px' }} className="requestGuarantor">
						<EligibilityRequestDetailsComponent transactionRequestDetails={this.sentGuarantor} demographicResponse={demographicGuarantor} title="Request - Guarantor" />
					</Col>
					<Col xs="6" style={{ padding: '0px', border: '1px solid #d1ccbf', borderTop: 'none' }}></Col>
				</Row>
				<Row style={{ marginTop: '0px', border: '1px solid #d1ccbf' }}>
					<Col xs="6" style={{ visibility: showDVLabel ? 'visible' : 'hidden', color: 'green', padding: '10px' }} className="dvInfoLabel">
						<strong>Information identified via demographic verification.</strong>
						<br />
						<strong>Demographic Confidence Score: {demographicConfidenceScore == '' || demographicConfidenceScore == null ? '' : demographicConfidenceScore == 'Y' ? 'High' : 'Low'}</strong>
					</Col>
					<Col xs="6" style={{ display: showPayorLabel ? '' : 'none', color: '#e17e27', padding: '10px' }} className="payorLabel">
						<strong>Information identified via payor.</strong>
					</Col>
				</Row>

				{(this.sentGuarantor ? Object.keys(demographicGuarantor).length > 0 : Object.keys(demographicIndividual).length > 0) && (demographicRedFlags || demographicWarnings) && (
					<Row style={{ marginTop: '0px' }}>
						<Col xs="12" style={{ padding: '0px' }}>
							{this.renderDemographicRedFlagAndWarningDetails(demographicRedFlags, demographicWarnings)}
						</Col>
					</Row>
				)}
			</Container>
		)
	}

	onDownloadX12File(): void {
		const response = this.props.response && this.props.response.eligibilityResponseTransaction

		if (response) {
			const raw271 = response.raw271
			const fileName = response.payer && `${response.payer.name}_271.txt`
			const blob = new Blob([raw271], { type: 'text/plain' })
			fileSaver.saveAs(blob, fileName)
		} else {
			alert('File not found')
		}
	}

	renderPatientResponseDetails(sent: IIndividual, received: IIndividual): JSX.Element {
		return (
			<Container className="patientdetails" style={{ borderBottom: this.sentGuarantor ? 'none' : '' }}>
				<Row className="header">
					<Col xs="4" className="px-0 dnldX12color">
						Response
					</Col>
					<Col xs="8" className="px-0 text-right dnldX12color">
						<span id="dnldX12Links" onClick={(): void => this.onDownloadX12File()}>
							Download X12
						</span>
					</Col>
				</Row>
				<Row id="firstName">
					<Col xs="5">
						<strong>First Name:</strong>
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.firstName, received.firstName) ? 'payorMismatchedText' : ''}>
						{received.firstName}
					</Col>
				</Row>
				<Row id="middleName">
					<Col xs="5">
						<strong>Middle Name:</strong>
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.middleName, received.middleName) ? 'payorMismatchedText' : ''}>
						{received.middleName}
					</Col>
				</Row>
				<Row id="lastName">
					<Col xs="5">
						<strong>Last Name:</strong>
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.lastName, received.lastName) ? 'payorMismatchedText' : ''}>
						{received.lastName}
					</Col>
				</Row>
				<Row id="gender">
					<Col xs="5">
						<strong>Gender:</strong>
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.gender, received.gender) ? 'payorMismatchedText' : ''}>
						{received.gender}
					</Col>
				</Row>
				<Row id="dateOfBirth">
					<Col xs="5">
						<strong>DOB:</strong>
					</Col>
					<Col xs="7" className={CommonUtils.datesAreEqual(sent.dateOfBirth, received.dateOfBirth) ? '' : 'payorMismatchedText'}>
						{CommonUtils.toDisplayDate(received.dateOfBirth)}
					</Col>
				</Row>
				<Row id="ssn">
					<Col xs="5">
						<strong>SSN:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.socialSecurityNumber, received.socialSecurityNumber) ? 'payorMismatchedText' : ''}>
						{received.socialSecurityNumber}
					</Col>
				</Row>
				<Row id="addressLine1">
					<Col xs="5">
						<strong>Address Line 1:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.address1, received.address1) ? 'payorMismatchedText' : ''}>
						{received.address1}
					</Col>
				</Row>
				<Row id="addressLine2">
					<Col xs="5">
						<strong>Address Line 2:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.address2, received.address2) ? 'payorMismatchedText' : ''}>
						{received.address2}
					</Col>
				</Row>
				<Row id="city">
					<Col xs="5">
						<strong>City:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.city, received.city) ? 'payorMismatchedText' : ''}>
						{received.city}
					</Col>
				</Row>
				<Row id="state">
					<Col xs="5">
						<strong>State:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.stateCodeId, received.stateCodeId) ? 'payorMismatchedText' : ''}>
						{typeof received.stateCode !== 'undefined' && received.stateCode !== null && received.stateCode.name}
					</Col>
				</Row>
				<Row id="zipCode">
					<Col xs="5">
						<strong>Zip Code:</strong>{' '}
					</Col>
					<Col xs="7" className={CommonUtils.isDifferent(sent.zip, received.zip) ? 'payorMismatchedText' : ''}>
						{received.zip}
					</Col>
				</Row>

				<Row id="subscriberId">
					<Col xs="5">
						<strong>Subscriber ID:</strong>{' '}
					</Col>
					<Col xs="7">{received.subscriberId}</Col>
				</Row>
			</Container>
		)
	}

	renderDemographicRedFlagAndWarningDetails(demographicRedFlags: string | undefined, demographicWarnings: string | undefined): JSX.Element {
		return (
			<Container className="patientdetails dvRedFlagsAndWarnings">
				{demographicRedFlags && (
					<div>
						<Row>
							<Col xs="12" className="redFlags">
								<img alt="Red Flags" src="img/icon-flag.png" title="RedFlags" className="imgRedFlagsAndWarnings"></img>
								<strong>Red Flags</strong>
							</Col>
						</Row>

						<Row className="redFlagsAndWarnings redFlagsLabel">
							<label>
								<strong>{demographicRedFlags}</strong>
							</label>
						</Row>
					</div>
				)}

				{demographicWarnings && (
					<div>
						<Row>
							<Col xs="12" className="warnings">
								<img alt="Warnings" src="img/icon-warning.png" title="Warnings" className="imgRedFlagsAndWarnings"></img>
								<strong>Warnings</strong>
							</Col>
						</Row>

						<Row className="redFlagsAndWarnings warningsLabel">
							<label>
								<strong>{demographicWarnings}</strong>
							</label>
						</Row>
					</div>
				)}
			</Container>
		)
	}

	setDVLabel(model, demographicModel): boolean {
		let showDVLabel = false
		if (model && demographicModel) {
			showDVLabel = CommonUtils.isDVDataDifferent(model.firstName, demographicModel.firstName) || CommonUtils.isDVDataDifferent(model.middleName, demographicModel.middleName) || CommonUtils.isDVDataDifferent(model.lastName, demographicModel.lastName) || CommonUtils.isDVDataDifferent(model.gender, demographicModel.gender) || CommonUtils.isDVDataDifferent(model.dateOfBirth, demographicModel.dateOfBirth) || CommonUtils.isDVDataDifferent(model.socialSecurityNumber, demographicModel.socialSecurityNumber) || CommonUtils.isDVAddressDifferent(model.address1, model.address2, demographicModel.address1) || CommonUtils.isDVDataDifferent(model.city, demographicModel.city) || CommonUtils.isDVDataDifferent(model.stateCodeId, demographicModel.stateCodeId) || CommonUtils.isZipCodeDifferent(model.zip, demographicModel.zip) || CommonUtils.isDVDataDifferent(model.phoneNumber, demographicModel.phoneNumber)
		}
		return showDVLabel
	}

	setPayorLabel(sent, received): boolean {
		return sent && received ? CommonUtils.isDifferent(sent.firstName, received.firstName) || CommonUtils.isDifferent(sent.middleName, received.middleName) || CommonUtils.isDifferent(sent.lastName, received.lastName) || CommonUtils.isDifferent(sent.gender, received.gender) || !CommonUtils.datesAreEqual(sent.dateOfBirth, received.dateOfBirth) || CommonUtils.isDifferent(sent.socialSecurityNumber, received.socialSecurityNumber) || CommonUtils.isDifferent(sent.address1, received.address1) || CommonUtils.isDifferent(sent.address2, received.address2) || CommonUtils.isDifferent(sent.city, received.city) || CommonUtils.isDifferent(sent.stateCodeId, received.stateCodeId) || CommonUtils.isDifferent(sent.zip, received.zip) : false
	}
}
